import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../../styles/modal.css';
import '../../styles/cicd-integration.css';
import axiosInstance from '../../api/axiosInstance';
// import { toast } from 'react-toastify'; // Commented out - using react-toast now
// import 'react-toastify/dist/ReactToastify.css'; // Commented out - using react-toast now
import { toast } from 'react-toast';
import { Pagination } from '../ui';

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

function CICDIntegrationModal({ isOpen, onClose, projectId, token, onPushToFramework }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFramework, setSelectedFramework] = useState('selenium');
  const [selectedTestSuites, setSelectedTestSuites] = useState([]);
  const [testSuites, setTestSuites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [cicdConfig, setCICDConfig] = useState({
    repositoryUrl: '',
    branch: 'main',
    commitMessage: 'Push test suites to automation framework',
    triggerPipeline: true,
    notifyOnCompletion: true
  });
  const [advancedOptions, setAdvancedOptions] = useState(false);
  const [frameworkConfig, setFrameworkConfig] = useState({
    selenium: {
      language: 'java',
      basePackage: 'com.example.tests',
      driver: 'chrome',
      implicitWait: 10,
      pageObjectModel: true
    },
    robot: {
      outputDir: 'test-output',
      variableFile: 'variables.py',
      includeSeleniumLibrary: true,
      logLevel: 'INFO'
    }
  });

  useEffect(() => {
    if (isOpen && projectId) {
      fetchTestSuites();
    }
  }, [isOpen, projectId]);

  useEffect(() => {
    if (isOpen && projectId) {
      const timeoutId = setTimeout(() => {
        fetchTestSuites();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery, pagination.page, pagination.pageSize]);

  const fetchTestSuites = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get(`/test_suite/list/${1}`, {
        params: {
          page: pagination.page,
          page_size: pagination.pageSize,
          ...(searchQuery ? { search: searchQuery } : {})
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const suites = response.test_suites || [];
      setTestSuites(suites);
      setPagination(prev => ({
        ...prev,
        total: response.total_count || 0
      }));
    } catch (err) {
      console.error('Error fetching test suites:', err);
      setError(err.response?.message || err.message || 'Failed to fetch test suites');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    fetchTestSuites();
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleNextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    try {
      setLoading(true);
      const frameworkMap = {
        'selenium': 1,
        'robot': 2
      };

      const languageMap = {
        'java': 1,
        'python': 2,
        'csharp': 3,
        'javascript': 4
      };

      const webdriverMap = {
        'chrome': 1,
        'firefox': 2,
        'edge': 3,
        'safari': 4
      };

      const payload = {
        framework: frameworkMap[selectedFramework],
        repo_url: cicdConfig.repositoryUrl,
        branch: cicdConfig.branch,
        commit_message: cicdConfig.commitMessage,
        trigger_pipeline: cicdConfig.triggerPipeline,
        notify: cicdConfig.notifyOnCompletion,
        language: languageMap[frameworkConfig.selenium.language] || 1,
        webdriver: webdriverMap[frameworkConfig.selenium.driver] || 1,
        namespace: frameworkConfig.selenium.basePackage || '',
        implicit_time: frameworkConfig.selenium.implicitWait || 10,
        pom: frameworkConfig.selenium.pageObjectModel,
        status: 1,
        suite_ids: selectedTestSuites
      };

      const integrationId = 0;

      const response = await axiosInstance.post(`/devops_integration/${integrationId}`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (onPushToFramework) {
        onPushToFramework({
          framework: selectedFramework,
          testSuites: selectedTestSuites.map(id => testSuites.find(s => s.suite_id === id)),
          cicdConfig,
          frameworkSpecificConfig: frameworkConfig[selectedFramework]
        });
      }

      showSuccessNotification('CI/CD integration configured successfully!');
      onClose();

    } catch (error) {
      console.error('Error configuring CI/CD integration:', error);

      let errorMessage = 'Failed to configure CI/CD integration';
      
      if (error.response?.status === 422) {
        const validationErrors = error.response.data.detail;
        errorMessage = `Validation error: ${validationErrors.map(err => err.msg).join(', ')}`;
      } else if (error.response?.status === 404) {
        errorMessage = 'API endpoint not found. Please check the integration ID or contact support.';
      } else {
        errorMessage = error.response?.data?.message || error.message || errorMessage;
      }
      
      setError(errorMessage);
      showErrorNotification(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const showSuccessNotification = (message) => {
    toast.success(message, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const showErrorNotification = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const handleTestSuiteSelection = (suiteId) => {
    setSelectedTestSuites(prev => {
      if (prev.includes(suiteId)) {
        return prev.filter(id => id !== suiteId);
      } else {
        return [...prev, suiteId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedTestSuites.length === testSuites.length) {
      setSelectedTestSuites([]);
    } else {
      setSelectedTestSuites(testSuites.map(suite => suite.suite_id));
    }
  };

  const updateFrameworkConfig = (framework, key, value) => {
    setFrameworkConfig(prev => ({
      ...prev,
      [framework]: {
        ...prev[framework],
        [key]: value
      }
    }));
  };

  const handleCICDConfigChange = (key, value) => {
    setCICDConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div data-rt-modal-overlay className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { const b = e.currentTarget.querySelector('[data-rt-modal-close], .close-btn, .modal-close, button[aria-label="Close"], button[aria-label="close"]'); if (b) b.click(); } }}>
      <div className="cicd-modal-container">
        <div className="cicd-modal-header">
          <h2>DevOps & CI/CD Integration</h2>
          <button className="cicd-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="cicd-modal-body">
          <div className="cicd-steps-container">
            <div className={`cicd-step ${currentStep === 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`} onClick={() => setCurrentStep(1)}>
              <span className="cicd-step-number">1</span>
              Framework
            </div>
            <div className={`cicd-step ${currentStep === 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`} onClick={() => currentStep > 1 ? setCurrentStep(2) : null}>
              <span className="cicd-step-number">2</span>
              Test Suites
            </div>
            <div className={`cicd-step ${currentStep === 3 ? 'active' : ''}`} onClick={() => currentStep > 2 ? setCurrentStep(3) : null}>
              <span className="cicd-step-number">3</span>
              Configuration
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className={`step-content ${currentStep === 1 ? 'active' : ''}`}>
              <div className="cicd-content">
                <h3>Select Automation Framework</h3>
                <p>Choose the automation framework you want to push your test suites to.</p>
                <div className="framework-selection">
                  <div className={`framework-option ${selectedFramework === 'selenium' ? 'selected' : ''}`} onClick={() => setSelectedFramework('selenium')}>
                    <div className="framework-icon">
                      <svg viewBox="0 0 24 24" width="40" height="40">
                        <rect width="24" height="24" fill="#43B02A" rx="4" />
                        <text x="12" y="17" fill="white" fontSize="14" fontWeight="bold" textAnchor="middle">Se</text>
                      </svg>
                    </div>
                    <div className="framework-info">
                      <h4>Selenium</h4>
                      <p>Web UI automation framework for browser testing.</p>
                    </div>
                  </div>
                  <div className={`framework-option ${selectedFramework === 'robot' ? 'selected' : ''}`} onClick={() => setSelectedFramework('robot')}>
                    <div className="framework-icon">
                      <svg viewBox="0 0 24 24" width="40" height="40">
                        <rect width="24" height="24" fill="#00A6D6" rx="4" />
                        <text x="12" y="17" fill="white" fontSize="14" fontWeight="bold" textAnchor="middle">RF</text>
                      </svg>
                    </div>
                    <div className="framework-info">
                      <h4>Robot Framework</h4>
                      <p>Keyword-driven testing framework.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={`step-content ${currentStep === 2 ? 'active' : ''}`}>
              <div className="cicd-content" style={{ width: '100%' }}>
                <h3>Select Test Suites to Push</h3>
                <p>Choose which test suites you want to push to the {selectedFramework === 'selenium' ? 'Selenium' : 'Robot Framework'} framework.</p>
                <div className="search-box">
                  <input type="text" placeholder="Search test suites..." value={searchQuery} onChange={handleSearch} />
                </div>
                {loading ? (
                  <div className="loading-indicator">Loading test suites...</div>
                ) : error ? (
                  <div className="error-message">
                    <p>{error}</p>
                    <button onClick={handleRetry}>Retry</button>
                  </div>
                ) : (
                  <div className="test-suite-selection">
                    <div className="selection-header">
                      <label>
                        <input type="checkbox" checked={selectedTestSuites.length === testSuites.length && testSuites.length > 0} onChange={handleSelectAll} />
                        Select All
                      </label>
                      <span>{selectedTestSuites.length} of {testSuites.length} selected</span>
                    </div>
                    <div className="test-suites-list">
                      {testSuites.length > 0 ? (
                        testSuites.map(suite => (
                          <div key={suite.suite_id} className="test-suite-item">
                            <label>
                              <input type="checkbox" checked={selectedTestSuites.includes(suite.suite_id)} onChange={() => handleTestSuiteSelection(suite.suite_id)} />
                              <span>{suite.suite_name}</span>
                              <span className="test-case-count">{suite.test_case_count || 0} test cases</span>
                            </label>
                          </div>
                        ))
                      ) : (
                        <div className="test-suite-item">
                          <p>No test suites found{searchQuery ? ` matching "${searchQuery}"` : ''}.</p>
                        </div>
                      )}
                    </div>
                    {pagination.total > pagination.pageSize && (
                      <Pagination
                        page={pagination.page}
                        pageSize={pagination.pageSize}
                        total={pagination.total}
                        onPageChange={handlePageChange}
                        showPageSize={false}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className={`step-content ${currentStep === 3 ? 'active' : ''}`}>
              <div className="cicd-content">
                <h3>CI/CD Configuration</h3>
                <p>Configure the repository and CI/CD settings for your test automation.</p>
                <div className="cicd-config-grid">
                  <div className="form-group">
                    <label>Repository URL</label>
                    <input type="text" value={cicdConfig.repositoryUrl} onChange={(e) => handleCICDConfigChange('repositoryUrl', e.target.value)} placeholder="https://github.com/your-org/your-repo.git" required />
                  </div>
                  <div className="form-group">
                    <label>Branch</label>
                    <input type="text" value={cicdConfig.branch} onChange={(e) => handleCICDConfigChange('branch', e.target.value)} placeholder="main" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Commit Message</label>
                  <input type="text" value={cicdConfig.commitMessage} onChange={(e) => handleCICDConfigChange('commitMessage', e.target.value)} placeholder="Push test suites to automation framework" />
                </div>
                <div className="checkbox-group">
                  <label>
                    <input type="checkbox" checked={cicdConfig.triggerPipeline} onChange={(e) => handleCICDConfigChange('triggerPipeline', e.target.checked)} />
                    Trigger CI/CD pipeline after push
                  </label>
                </div>
                <div className="checkbox-group">
                  <label>
                    <input type="checkbox" checked={cicdConfig.notifyOnCompletion} onChange={(e) => handleCICDConfigChange('notifyOnCompletion', e.target.checked)} />
                    Notify me when complete
                  </label>
                </div>
                <div className="advanced-toggle" onClick={() => setAdvancedOptions(!advancedOptions)}>
                  <h3>Advanced Framework Options</h3>
                  <span>{advancedOptions ? '▼' : '▶'}</span>
                </div>
                {advancedOptions && (
                  <div className="advanced-options">
                    {selectedFramework === 'selenium' && (
                      <>
                        <div className="cicd-config-grid">
                          <div className="form-group">
                            <label>Programming Language</label>
                            <select value={frameworkConfig.selenium.language} onChange={(e) => updateFrameworkConfig('selenium', 'language', e.target.value)}>
                              <option value="java">Java</option>
                              <option value="python">Python</option>
                              <option value="csharp">C#</option>
                              <option value="javascript">JavaScript</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Default WebDriver</label>
                            <select value={frameworkConfig.selenium.driver} onChange={(e) => updateFrameworkConfig('selenium', 'driver', e.target.value)}>
                              <option value="chrome">Chrome</option>
                              <option value="firefox">Firefox</option>
                              <option value="edge">Edge</option>
                              <option value="safari">Safari</option>
                            </select>
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Base Package/Namespace</label>
                          <input type="text" value={frameworkConfig.selenium.basePackage} onChange={(e) => updateFrameworkConfig('selenium', 'basePackage', e.target.value)} placeholder="com.example.tests" />
                        </div>
                        <div className="cicd-config-grid">
                          <div className="form-group">
                            <label>Implicit Wait (seconds)</label>
                            <input type="number" value={frameworkConfig.selenium.implicitWait} onChange={(e) => updateFrameworkConfig('selenium', 'implicitWait', parseInt(e.target.value))} min="0" max="60" />
                          </div>
                          <div className="checkbox-group" style={{ marginTop: '32px' }}>
                            <label>
                              <input type="checkbox" checked={frameworkConfig.selenium.pageObjectModel} onChange={(e) => updateFrameworkConfig('selenium', 'pageObjectModel', e.target.checked)} />
                              Use Page Object Model pattern
                            </label>
                          </div>
                        </div>
                      </>
                    )}
                    {selectedFramework === 'robot' && (
                      <>
                        <div className="cicd-config-grid">
                          <div className="form-group">
                            <label>Output Directory</label>
                            <input type="text" value={frameworkConfig.robot.outputDir} onChange={(e) => updateFrameworkConfig('robot', 'outputDir', e.target.value)} placeholder="test-output" />
                          </div>
                          <div className="form-group">
                            <label>Variable File</label>
                            <input type="text" value={frameworkConfig.robot.variableFile} onChange={(e) => updateFrameworkConfig('robot', 'variableFile', e.target.value)} placeholder="variables.py" />
                          </div>
                        </div>
                        <div className="cicd-config-grid">
                          <div className="form-group">
                            <label>Log Level</label>
                            <select value={frameworkConfig.robot.logLevel} onChange={(e) => updateFrameworkConfig('robot', 'logLevel', e.target.value)}>
                              <option value="INFO">INFO</option>
                              <option value="DEBUG">DEBUG</option>
                              <option value="TRACE">TRACE</option>
                              <option value="WARN">WARN</option>
                              <option value="ERROR">ERROR</option>
                            </select>
                          </div>
                          <div className="checkbox-group" style={{ marginTop: '32px' }}>
                            <label>
                              <input type="checkbox" checked={frameworkConfig.robot.includeSeleniumLibrary} onChange={(e) => updateFrameworkConfig('robot', 'includeSeleniumLibrary', e.target.checked)} />
                              Include SeleniumLibrary
                            </label>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="cicd-modal-footer">
              <div className="footer-buttons">
                {currentStep > 1 && (
                  <button type="button" className="back-button" onClick={handlePrevStep}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="19" y1="12" x2="5" y2="12"></line>
                      <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                    Back
                  </button>
                )}
                <button type="button" className="cancel-button" onClick={onClose}>Cancel</button>
              </div>
              <div className="footer-buttons">
                {currentStep < 3 ? (
                  <button type="button" className="next-button" onClick={handleNextStep} disabled={currentStep === 2 && selectedTestSuites.length === 0}>
                    Next
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </button>
                ) : (
                  <button type="submit" className="submit-button" disabled={selectedTestSuites.length === 0 || !cicdConfig.repositoryUrl || loading}>
                    Push to {selectedFramework === 'selenium' ? 'Selenium' : 'Robot Framework'}
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14"></path>
                      <path d="M12 5l7 7-7 7"></path>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CICDIntegrationModal;